import React, { useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { api } from "../lib/api";
import { trackEvent } from "../lib/analytics";

/**
 * Full-width rotating banner strip shown on the home page between the
 * latest-posts feed and the Explore section. Admin manages slides
 * (image + link URL) and the enabled/auto-rotate flags in
 * Admin → Homepage strip. Renders nothing when disabled or empty.
 */
export default function HomeStrip() {
  const [config, setConfig] = useState(null);
  const [index, setIndex] = useState(0);
  const timerRef = useRef(null);
  const randomizedRef = useRef(false);

  useEffect(() => {
    api
      .get("/settings/homepage-strip")
      .then((r) => setConfig(r.data))
      .catch(() => setConfig(null));
  }, []);

  const slides = (config?.slides || []).filter((s) => s && s.image_url);

  // Start on a random slide (instead of always the first) so repeat
  // visitors on the same day aren't shown the same banner every time.
  useEffect(() => {
    if (randomizedRef.current || slides.length < 2) return;
    randomizedRef.current = true;
    setIndex(Math.floor(Math.random() * slides.length));
  }, [slides.length]);

  useEffect(() => {
    if (!config?.auto_rotate || slides.length < 2) return undefined;
    timerRef.current = window.setInterval(() => {
      setIndex((i) => (i + 1) % slides.length);
    }, config.interval_ms || 5000);
    return () => window.clearInterval(timerRef.current);
  }, [config?.auto_rotate, config?.interval_ms, slides.length]);

  if (!config?.enabled || slides.length === 0) return null;

  // Independent desktop/mobile visibility toggles from Admin → Homepage
  // strip. Both default true (existing behaviour) when unset.
  const showOnMobile = config.show_on_mobile !== false;
  const showOnDesktop = config.show_on_desktop !== false;
  if (!showOnMobile && !showOnDesktop) return null;
  const visibilityClass = !showOnMobile ? "hidden sm:block" : !showOnDesktop ? "sm:hidden" : "";

  const safeIndex = index % slides.length;
  const slide = slides[safeIndex];

  const go = (delta) => {
    setIndex((i) => (i + delta + slides.length) % slides.length);
  };

  return (
    <section
      data-testid="home-strip"
      className={`relative mx-auto max-w-7xl px-5 pt-4 sm:px-8 sm:pt-0 ${visibilityClass}`}
    >
      <div className="relative overflow-hidden rounded-md border border-black/10">
        <a
          href={slide.link_url || "#"}
          target={slide.open_in_new_tab !== false ? "_blank" : "_self"}
          rel="noreferrer"
          data-testid="home-strip-slide-link"
          className="block"
          onClick={() =>
            trackEvent("merch_banner_click", {
              banner_location: "pwa_top",
              destination: "shopify",
            })
          }
        >
          <img
            src={slide.image_url}
            alt={slide.alt || ""}
            data-testid="home-strip-image"
            className="aspect-[2000/409] w-full object-cover sm:aspect-[2000/205]"
          />
        </a>

        {slides.length > 1 ? (
          <>
            <button
              type="button"
              onClick={() => go(-1)}
              aria-label="Previous slide"
              data-testid="home-strip-prev"
              className="absolute left-2 top-1/2 inline-flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full bg-black/40 text-white transition-colors hover:bg-black/60"
            >
              <ChevronLeft size={16} strokeWidth={2.5} />
            </button>
            <button
              type="button"
              onClick={() => go(1)}
              aria-label="Next slide"
              data-testid="home-strip-next"
              className="absolute right-2 top-1/2 inline-flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full bg-black/40 text-white transition-colors hover:bg-black/60"
            >
              <ChevronRight size={16} strokeWidth={2.5} />
            </button>
            <div className="absolute bottom-2 left-1/2 flex -translate-x-1/2 gap-1.5">
              {slides.map((s, i) => (
                <button
                  key={s.id || i}
                  type="button"
                  onClick={() => setIndex(i)}
                  aria-label={`Go to slide ${i + 1}`}
                  data-testid={`home-strip-dot-${i}`}
                  className={`h-1.5 w-1.5 rounded-full transition-colors ${
                    i === safeIndex ? "bg-white" : "bg-white/40"
                  }`}
                />
              ))}
            </div>
          </>
        ) : null}
      </div>
    </section>
  );
}
